'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  FilterFn,
} from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowUp, ArrowDown, RefreshCw, Activity, AlertTriangle, Users, Filter } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatTimeAgo } from '@/i18n/utils'
import Link from 'next/link'
import { toast } from 'sonner'

interface PatientRisk {
  patientId: string
  name: string
  room: string
  consciousness: 'alert' | 'confused' | 'drowsy'
  riskScore: number
  isAlerting: boolean
  timestamp: string
  previousRiskScore?: number
}

interface RiskLeaderboardProps {
  wardId?: string
  autoRefresh?: boolean
  refreshInterval?: number
}

export function RiskLeaderboard({ 
  wardId = '3A', 
  autoRefresh = true,
  refreshInterval = 30000 
}: RiskLeaderboardProps) {
  const [data, setData] = useState<PatientRisk[]>([])
  const [loading, setLoading] = useState(true)
  const [sorting, setSorting] = useState<SortingState>([{ id: 'riskScore', desc: true }])
  const [consciousnessFilter, setConsciousnessFilter] = useState<string>('all')
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())
  const eventSourceRef = useRef<EventSource | null>(null)
  const previousDataRef = useRef<Map<string, number>>(new Map())

  // Consciousness labels in Korean
  const consciousnessLabels = {
    alert: '각성',
    confused: '혼돈',
    drowsy: '기면',
    all: '전체'
  }

  // Fetch risk data
  const fetchRiskData = async () => {
    try {
      const params = new URLSearchParams({
        ward_id: wardId,
        top: '10',  // 위험도 TOP 10
        sort_by: 'risk'
      })
      
      if (consciousnessFilter !== 'all') {
        params.append('consciousness', consciousnessFilter)
      }

      const response = await fetch(`/api/v1/inference/risk?${params}`)
      const result = await response.json()

      if (result.predictions) {
        // Store previous risk scores for comparison
        const newPrevData = new Map<string, number>()
        data.forEach(item => {
          newPrevData.set(item.patientId, item.riskScore)
        })
        previousDataRef.current = newPrevData

        // Update data with previous scores
        const updatedPredictions = result.predictions.map((pred: PatientRisk) => ({
          ...pred,
          previousRiskScore: previousDataRef.current.get(pred.patientId)
        }))

        setData(updatedPredictions)
        setLastUpdate(new Date())
      }
    } catch (error) {
      console.error('Failed to fetch risk data:', error)
      toast.error('위험도 데이터를 불러오는데 실패했습니다')
    } finally {
      setLoading(false)
    }
  }

  // Setup SSE for real-time updates
  useEffect(() => {
    if (!autoRefresh) return

    const connectSSE = () => {
      eventSourceRef.current = new EventSource('/api/v1/events/stream')

      eventSourceRef.current.onmessage = (event) => {
        try {
          const alertData = JSON.parse(event.data)
          
          // Update specific patient's risk score
          setData(prev => {
            const newData = [...prev]
            const index = newData.findIndex(p => p.patientId === alertData.patientId)
            
            if (index !== -1) {
              const previousScore = newData[index].riskScore
              newData[index] = {
                ...newData[index],
                riskScore: alertData.riskScore,
                previousRiskScore: previousScore,
                isAlerting: true,
                timestamp: alertData.timestamp
              }
            }
            
            return newData
          })
        } catch (error) {
          console.error('Failed to parse SSE message:', error)
        }
      }

      eventSourceRef.current.onerror = () => {
        console.error('SSE connection error, reconnecting...')
        eventSourceRef.current?.close()
        setTimeout(connectSSE, 5000)
      }
    }

    connectSSE()

    return () => {
      eventSourceRef.current?.close()
    }
  }, [autoRefresh])

  // Initial load and periodic refresh
  useEffect(() => {
    fetchRiskData()

    if (autoRefresh) {
      const interval = setInterval(fetchRiskData, refreshInterval)
      return () => clearInterval(interval)
    }
  }, [wardId, consciousnessFilter])

  // Table columns definition
  const columns = useMemo<ColumnDef<PatientRisk>[]>(
    () => [
      {
        accessorKey: 'rank',
        header: '순위',
        cell: ({ row }) => {
          const rank = row.index + 1
          return (
            <div className={cn(
              "font-bold text-lg",
              rank === 1 && "text-red-600",
              rank === 2 && "text-red-500",
              rank === 3 && "text-red-400"
            )}>
              {rank}
            </div>
          )
        },
        size: 60,
      },
      {
        accessorKey: 'patientId',
        header: '환자 ID',
        cell: ({ row }) => (
          <Link href={`/patients/${row.original.patientId}`} className="font-mono hover:underline">
            {row.original.patientId}
          </Link>
        ),
        size: 100,
      },
      {
        accessorKey: 'name',
        header: '환자명',
        cell: ({ row }) => (
          <Link href={`/patients/${row.original.patientId}`} className="hover:underline">
            {row.original.name}
          </Link>
        ),
      },
      {
        accessorKey: 'room',
        header: '병실',
        cell: ({ row }) => (
          <Badge variant="outline">{row.original.room}</Badge>
        ),
        size: 80,
      },
      {
        accessorKey: 'consciousness',
        header: '의식상태',
        cell: ({ row }) => {
          const level = row.original.consciousness
          return (
            <Badge 
              variant={level === 'alert' ? 'default' : level === 'confused' ? 'secondary' : 'outline'}
            >
              {consciousnessLabels[level]}
            </Badge>
          )
        },
        size: 100,
      },
      {
        accessorKey: 'riskScore',
        header: '위험도',
        cell: ({ row }) => {
          const score = row.original.riskScore
          const prevScore = row.original.previousRiskScore
          const delta = prevScore ? score - prevScore : 0
          
          return (
            <div className="flex items-center gap-2">
              <span className={cn(
                "font-bold text-lg",
                score > 0.75 ? "text-red-600" : 
                score > 0.5 ? "text-orange-600" : 
                "text-green-600"
              )}>
                {(score * 100).toFixed(1)}%
              </span>
              {delta !== 0 && prevScore && (
                <span className={cn(
                  "flex items-center text-xs",
                  delta > 0 ? "text-red-500" : "text-green-500"
                )}>
                  {delta > 0 ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : (
                    <ArrowDown className="h-3 w-3" />
                  )}
                  {Math.abs(delta * 100).toFixed(1)}
                </span>
              )}
            </div>
          )
        },
        size: 150,
      },
      {
        accessorKey: 'status',
        header: '상태',
        cell: ({ row }) => {
          if (row.original.isAlerting) {
            return (
              <Badge variant="destructive" className="animate-pulse">
                <AlertTriangle className="h-3 w-3 mr-1" />
                경보
              </Badge>
            )
          }
          return <Badge variant="secondary">정상</Badge>
        },
        size: 100,
      },
      {
        accessorKey: 'timestamp',
        header: '업데이트',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatTimeAgo(new Date(row.original.timestamp))}
          </span>
        ),
        size: 100,
      },
    ],
    []
  )

  // Table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              실시간 위험도 TOP 10
            </CardTitle>
            <CardDescription>
              병동 {wardId} - {formatTimeAgo(lastUpdate)} 업데이트
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={consciousnessFilter} onValueChange={setConsciousnessFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="의식상태" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체</SelectItem>
                <SelectItem value="alert">각성</SelectItem>
                <SelectItem value="confused">혼돈</SelectItem>
                <SelectItem value="drowsy">기면</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRiskData}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
              새로고침
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && data.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p>해당 조건에 맞는 환자가 없습니다</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        style={{ width: header.column.getSize() }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    className={cn(
                      "transition-all duration-300",
                      row.original.isAlerting && "bg-red-50 dark:bg-red-950/20",
                      index === 0 && "bg-yellow-50 dark:bg-yellow-950/20"
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Summary Stats */}
        {data.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                {data.filter(d => d.riskScore > 0.75).length}
              </p>
              <p className="text-xs text-muted-foreground">고위험</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">
                {data.filter(d => d.riskScore > 0.5 && d.riskScore <= 0.75).length}
              </p>
              <p className="text-xs text-muted-foreground">중간위험</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {data.filter(d => d.riskScore <= 0.5).length}
              </p>
              <p className="text-xs text-muted-foreground">저위험</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}